// ============================================
// Motor de Tareas por Código
// ============================================
// Cualquier ejercicio de la app puede convertirse en tarea.
// El profesor genera un código corto; el estudiante lo ingresa para acceder.

// ─── Tipos de tarea (uno por módulo de la app) ───
export type TaskType =
  | "chord"
  | "scale"
  | "mode"
  | "melody"
  | "song"
  | "note"
  | "finger"
  | "custom";

// ─── Configuración específica de cada tipo ───
export interface ChordTaskConfig {
  root: string;
  quality: string;
  voicingMode: "classic" | "explorer";
  voicingIndex: number;
  detectionMode: "chord" | "arpeggio";
}

export interface ScaleTaskConfig {
  rootNote: string;
  scaleType: string;
  octaves: number;
  direction: "asc" | "desc" | "both";
  pattern: string;
}

export interface ModeTaskConfig {
  tonica: string;
  brillo: number;
  pattern: string;
  octaves: number;
  direction: "asc" | "desc" | "both";
}

export interface MelodyTaskConfig {
  melodyId: string;
  melodyTitle: string;
}

export interface SongTaskConfig {
  songTitle: string;
  musicXml: string;
  youtubeUrl?: string;
  tempo?: number;
}

export interface NoteTaskConfig {
  targetNote: string;
  noteCount: number;
}

export interface FingerTaskConfig {
  exerciseId: string;
  exerciseTitle: string;
  bpm: number;
}

export interface CustomTaskConfig {
  description: string;
}

export type TaskConfig =
  | ChordTaskConfig
  | ScaleTaskConfig
  | ModeTaskConfig
  | MelodyTaskConfig
  | SongTaskConfig
  | NoteTaskConfig
  | FingerTaskConfig
  | CustomTaskConfig;

// ─── Tarea guardada en Firestore ───
export interface Task {
  id: string;              // código corto (ej: "X7K9P2")
  teacherId: string;       // UID Firebase del profesor
  teacherName?: string;
  type: TaskType;
  title: string;
  description?: string;
  config: TaskConfig;
  validation: {
    useAudio: boolean;
    maxAttempts: number;
    showFeedback: boolean;
  };
  createdAt: number;       // timestamp ms
  expiresAt?: number;      // timestamp ms (opcional)
  /** Si está definido, solo miembros de esa clase pueden ver/entregar */
  classroomId?: string;
  /** Si tiene elementos, solo esos UIDs (deben estar en classroomId si aplica) */
  assignedStudentIds?: string[];
}

// ─── Resultado de un estudiante ───
export interface StudentProfile {
  uid: string;
  firstName: string;
  lastName: string;
  rut?: string;
  accountType: "estudiante";
  createdAt: number;
}

export interface TaskResult {
  taskId: string;
  teacherId: string;
  studentName?: string;
  attempts: TaskAttempt[];
  bestScore: number;       // 0-1
  completedAt: number;     // timestamp ms
  durationMs: number;      // tiempo total practicado
}

export interface TaskAttempt {
  timestamp: number;
  score: number;           // 0-1
  detectedNotes: string[]; // notas detectadas
  expectedNotes: string[]; // notas esperadas
  feedback: string;
}

// ─── Generador de códigos ───
const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // sin 0, O, I, 1 para evitar confusión
const CODE_LENGTH = 6;

export function isTaskAssignedToStudent(task: Task, studentId: string): boolean {
  if (!task.assignedStudentIds?.length) return true;
  return task.assignedStudentIds.includes(studentId);
}

export function generateTaskCode(): string {
  let code = "";
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return code;
}

// ─── Serialización del estado de página a config ───
export function serializeChordState(
  root: string,
  quality: string,
  voicingMode: "classic" | "explorer",
  voicingIndex: number,
  detectionMode: "chord" | "arpeggio"
): ChordTaskConfig {
  return { root, quality, voicingMode, voicingIndex, detectionMode };
}

export function serializeScaleState(
  rootNote: string,
  scaleType: string,
  octaves: number,
  direction: "asc" | "desc" | "both",
  pattern: string
): ScaleTaskConfig {
  return { rootNote, scaleType, octaves, direction, pattern };
}

export function serializeModeState(
  tonica: string,
  brillo: number,
  pattern: string,
  octaves: number,
  direction: "asc" | "desc" | "both"
): ModeTaskConfig {
  return { tonica, brillo, pattern, octaves, direction };
}

export function omitUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as T;
}

export function serializeSongState(
  songTitle: string,
  musicXml: string,
  opts?: { youtubeUrl?: string; tempo?: number }
): SongTaskConfig {
  return omitUndefined({
    songTitle,
    musicXml,
    youtubeUrl: opts?.youtubeUrl?.trim() || undefined,
    tempo: opts?.tempo,
  }) as SongTaskConfig;
}

// ─── Helpers de UI ───
export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  chord: "Acorde",
  scale: "Escala",
  mode: "Modo",
  melody: "Melodía",
  song: "Canción (MusicXML)",
  note: "Notas en mástil",
  finger: "Gimnasio de dedos",
  custom: "Personalizado",
};

export const TASK_TYPE_ICONS: Record<TaskType, string> = {
  chord: "◉",
  scale: "◈",
  mode: "◐",
  melody: "♪",
  song: "🎼",
  note: "♩",
  finger: "✋",
  custom: "⚙",
};

export const TASK_TYPE_COLORS: Record<TaskType, string> = {
  chord: "#F59E0B",
  scale: "#E11D48",
  mode: "#06B6D4",
  melody: "#8B5CF6",
  song: "#c9405a",
  note: "#22C55E",
  finger: "#EC4899",
  custom: "#64748B",
};
